/**
 * Quick order.
 *
 * Search runs against Shopify's predictive search endpoint, which for a
 * signed-in B2B buyer is already scoped to that company location's catalog and
 * already returns that catalog's prices. Neither the scoping nor the pricing is
 * reimplemented here: a second source of truth for money is how a trade site
 * quotes a figure it cannot honour.
 *
 * Adding is one POST of every line to /cart/add.js. Shopify validates each
 * variant against the catalog, so a line the buyer may not order fails there
 * and is reported, rather than being filtered out silently in here.
 */
(() => {
  const root = document.querySelector('[data-dopa-quick-order]');
  if (!root) return;

  const searchUrl = root.dataset.searchUrl;
  const addUrl = root.dataset.cartAddUrl;
  const cartUrl = root.dataset.cartUrl;

  const input = root.querySelector('[data-dopa-quick-search]');
  const results = root.querySelector('[data-dopa-quick-results]');
  const tbody = root.querySelector('[data-dopa-quick-lines]');
  const empty = root.querySelector('[data-dopa-quick-empty]');
  const countEl = root.querySelector('[data-dopa-quick-count]');
  const subtotalEl = root.querySelector('[data-dopa-quick-subtotal]');
  const submit = root.querySelector('[data-dopa-quick-submit]');
  const status = root.querySelector('[data-dopa-quick-status]');
  const form = root.querySelector('[data-dopa-quick-form]');
  const pasteBtn = root.querySelector('[data-dopa-quick-paste]');

  const lines = new Map();
  let currency = null;

  const money = (cents) => {
    if (cents == null) return '—';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency', currency: currency || 'INR', maximumFractionDigits: 0,
      }).format(cents / 100);
    } catch {
      return String(Math.round(cents / 100));
    }
  };

  // Predictive search reports price differently across endpoint versions.
  function priceOf(v) {
    if (typeof v.price === 'number') return v.price;
    if (v.price && typeof v.price.amount === 'string') return Math.round(parseFloat(v.price.amount) * 100);
    if (typeof v.price === 'string') return Math.round(parseFloat(v.price.replace(/[^0-9.]/g, '')) * 100);
    return null;
  }

  const text = (el, cls, value) => {
    const s = document.createElement('span');
    s.className = cls;
    s.textContent = value;
    el.appendChild(s);
    return s;
  };

  function render() {
    tbody.textContent = '';
    let n = 0;
    let subtotal = 0;

    for (const [id, line] of lines) {
      n += 1;
      subtotal += (line.price || 0) * line.qty;

      const tr = document.createElement('tr');

      const idx = document.createElement('td');
      idx.setAttribute('data-tabular', '');
      idx.textContent = String(n);
      tr.appendChild(idx);

      const name = document.createElement('td');
      text(name, 'dopa-quick__title', line.title);
      if (line.variantTitle) text(name, 'dopa-quick__variant', line.variantTitle);
      tr.appendChild(name);

      const sku = document.createElement('td');
      sku.className = 'dopa-quick__sku';
      sku.textContent = line.sku || '—';
      tr.appendChild(sku);

      // Quantity is a real number input: tab-and-type is the whole point of
      // this page, so it must not be a widget that swallows the keyboard.
      const qtyCell = document.createElement('td');
      const qty = document.createElement('input');
      qty.type = 'number';
      qty.min = '1';
      qty.step = '1';
      qty.value = String(line.qty);
      qty.className = 'dopa-form__input dopa-quick__qty tap';
      qty.setAttribute('aria-label', 'Cases of ' + line.title);
      qty.dataset.qtyFor = id;
      qty.addEventListener('input', () => {
        const v = parseInt(qty.value, 10);
        line.qty = Number.isFinite(v) && v > 0 ? v : 1;
        render();
        const again = tbody.querySelector('[data-qty-for="' + id + '"]');
        if (again) { again.focus(); again.select(); }
      });
      qtyCell.appendChild(qty);
      tr.appendChild(qtyCell);

      const total = document.createElement('td');
      total.className = 'dopa-quick__num';
      total.setAttribute('data-tabular', '');
      total.textContent = money((line.price || 0) * line.qty);
      tr.appendChild(total);

      const removeCell = document.createElement('td');
      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'dopa-quick__remove tap';
      remove.textContent = '×';
      remove.setAttribute('aria-label', 'Remove ' + line.title);
      remove.addEventListener('click', () => { lines.delete(id); render(); });
      removeCell.appendChild(remove);
      tr.appendChild(removeCell);

      tbody.appendChild(tr);
    }

    countEl.textContent = String(n);
    subtotalEl.textContent = n ? money(subtotal) : '—';
    empty.hidden = n > 0;
    submit.disabled = n === 0;
  }

  function addLine(v) {
    const id = String(v.id);
    if (lines.has(id)) lines.get(id).qty += 1;
    else lines.set(id, { id, qty: 1, title: v.title, variantTitle: v.variantTitle, sku: v.sku, price: v.price });
    render();
  }

  /* ------------------------------------------------------------- search */

  async function fetchProducts(term) {
    const url = searchUrl + '?q=' + encodeURIComponent(term) +
      '&resources[type]=product&resources[limit]=8';
    const r = await fetch(url, { headers: { Accept: 'application/json' } });
    if (!r.ok) throw new Error(String(r.status));
    const data = await r.json();
    return (data.resources && data.resources.results && data.resources.results.products) || [];
  }

  let timer = null;
  async function search(term) {
    if (term.trim().length < 2) { results.hidden = true; return; }
    try {
      const products = await fetchProducts(term);
      results.textContent = '';
      if (!products.length) {
        const li = document.createElement('li');
        li.className = 'dopa-quick__result--none';
        li.textContent = 'Nothing matches that.';
        results.appendChild(li);
      }
      for (const p of products) {
        for (const v of (p.variants || []).slice(0, 4)) {
          currency = currency || (v.price && v.price.currency) || null;
          const li = document.createElement('li');
          const btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'dopa-quick__result tap';
          text(btn, 'dopa-quick__title', p.title);
          if (v.title && v.title !== 'Default Title') text(btn, 'dopa-quick__variant', v.title);
          if (v.sku) text(btn, 'dopa-quick__sku', v.sku);
          btn.addEventListener('click', () => {
            addLine({
              id: v.id, title: p.title,
              variantTitle: v.title === 'Default Title' ? '' : v.title,
              sku: v.sku, price: priceOf(v),
            });
            input.value = '';
            results.hidden = true;
            input.focus();
          });
          li.appendChild(btn);
          results.appendChild(li);
        }
      }
      results.hidden = false;
    } catch {
      status.textContent = 'Search is not answering. Try again, or paste a list of SKUs.';
    }
  }

  input.addEventListener('input', () => {
    clearTimeout(timer);
    timer = setTimeout(() => search(input.value), 220);
  });
  input.addEventListener('keydown', (e) => { if (e.key === 'Escape') results.hidden = true; });

  /* -------------------------------------------------------- paste a list */

  async function lookup(sku) {
    try {
      for (const p of await fetchProducts(sku)) {
        for (const v of (p.variants || [])) {
          if (v.sku && v.sku.toLowerCase() === sku.toLowerCase()) {
            return {
              id: v.id, title: p.title,
              variantTitle: v.title === 'Default Title' ? '' : v.title,
              sku: v.sku, price: priceOf(v),
            };
          }
        }
      }
    } catch { /* reported by the caller */ }
    return null;
  }

  pasteBtn.addEventListener('click', async () => {
    const raw = window.prompt('Paste one SKU per line.');
    if (!raw) return;
    const skus = raw.split(/[\n,]/).map((s) => s.trim()).filter(Boolean);
    const missed = [];
    for (const sku of skus) {
      const found = await lookup(sku);
      if (found) addLine(found); else missed.push(sku);
    }
    // Name what did not match. A silent partial add is how a kitchen runs out.
    status.textContent = missed.length
      ? 'Added ' + (skus.length - missed.length) + ' of ' + skus.length + '. Not found: ' + missed.join(', ')
      : 'Added ' + skus.length + ' lines.';
  });

  /* ---------------------------------------------------------- add to cart */

  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    if (!lines.size) return;
    submit.disabled = true;
    status.textContent = 'Adding…';

    const items = [...lines.values()].map((l) => ({ id: Number(l.id), quantity: l.qty }));
    try {
      const r = await fetch(addUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });
      const data = await r.json();
      if (!r.ok) {
        status.textContent = data.description || data.message || 'That order was not accepted.';
        submit.disabled = false;
        return;
      }
      window.location.href = cartUrl;
    } catch {
      status.textContent = 'The cart is not answering. Nothing was added.';
      submit.disabled = false;
    }
  });

  render();
})();
